import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import type { List, RecordOf } from 'immutable';
import type { SplitItemProps, SplitProps, TransactionProps, TransactionRecord } from './transactionsTypes';

// -------------------------------------------------------
export function isUnbalancedSplitTxn(txn: TransactionProps) {
  if (transactionsUtils.isSplitTxn(txn)) {
    return Number(txn.amount).toFixed(2) !== sumSplitItems(txn.split).toFixed(2);
  }
  return false;
}

export const splitTxnRemainder = (txn: TransactionProps) =>
  Number((Number(txn.amount) - sumSplitItems(txn.split)).toFixed(2));

export function addRemainderToSplit(txn: TransactionRecord, remainder: number) {
  if (!txn.split || !txn.split.items) {
    // eslint-disable-next-line no-console
    console.error('Txn has no split or no split items');
    return txn;
  }

  const itemIndex = txn.split.items.findIndex((item) =>
    Boolean(item.coa && item.coa.type === 'UNCATEGORIZED' && Number(item.coa.id) === 0),
  );

  if (itemIndex !== -1) {
    assert(!Array.isArray(txn.split.items), 'Split Items not immutable - Please tell Chris what you just did');
    const items = txn.split.items as List<RecordOf<SplitItemProps>>;
    // Non-null assertion because we are already checking the index from findIndex
    let item = items.get(itemIndex)!;
    item = item.set('amount', Number(item.amount) + remainder);
    const newItems = items.set(itemIndex, item);
    return txn.setIn(['split', 'items'], newItems);
  }
  // need to create the uncategorized row
  const newItem = transactionsTypes.mkSplitItem({
    amount: remainder,
    coa: chartOfAccountsTypes.mkChartOfAccount({ id: '0', type: 'UNCATEGORIZED' }),
  });
  return txn.setIn(['split', 'items'], txn.split.items.push(newItem));
}

// -------------------------------------------------------
export function consolidateUncatSplitItems(txn: TransactionRecord) {
  let updatedTxn = txn;
  if (!txn.split || !txn.split.items) {
    return updatedTxn;
  }
  if (transactionsUtils.isSplitTxn(txn)) {
    let total = 0;
    const consolidatedItems = (txn.split.items as List<RecordOf<SplitItemProps>>)
      .filter((item: SplitItemProps) => {
        // total uncategorized amount while removing them
        if (!item.coa || item.coa.type === 'UNCATEGORIZED') {
          total += Number(item.amount);
          return false;
        }
        return true;
      })
      .push(transactionsTypes.mkSplitItem({ coa: { id: '0', type: 'UNCATEGORIZED' }, amount: total })); // add total back

    // if everything was uncategorized so only one splitItem left, revert to normal coa
    if (consolidatedItems?.size === 1) {
      updatedTxn = updatedTxn.merge({ split: null, coa: { id: '0', type: 'UNCATEGORIZED' } });
    } else {
      updatedTxn = updatedTxn.setIn(['split', 'items'], consolidatedItems);
    }
  }
  return updatedTxn;
}

// # ================================================================ #
// # - - -            UI Split logic/functions                  - - - #
// # ================================================================ #

// ----------------
// adjustSplitLine
// ----------------
// given a txn and the index of the splitItem just edited, perform the balancing
// and return the updated split
//
// expects an immutable split
export function adjustSplitLine(txn: Pick<TransactionProps, 'amount' | 'split'>, index: number) {
  assert(index !== undefined, 'FORGOT TO PASS IN AN INDEX');
  let newSplit = txn.split;

  if (!newSplit || !newSplit.items) {
    return newSplit;
  }

  const items = newSplit.items;

  const diff = Number((Number(txn.amount) - sumSplitItems(newSplit)).toFixed(2));

  function adjustAmountAtIndex(i: number) {
    if (!newSplit || !newSplit.items) {
      return;
    }

    const otherItem = 'get' in newSplit.items ? newSplit.items.get(i) : newSplit.items[i];

    if (!otherItem) {
      return;
    }

    const newAmount = Number(Number(Number(otherItem.amount) + diff).toFixed(2));

    if ('setIn' in newSplit) {
      newSplit = newSplit.setIn(['items', i, 'amount'], newAmount);
    } else if (Array.isArray(newSplit.items)) {
      newSplit.items[i].amount = newAmount;
    } else {
      newSplit.items = newSplit.items.setIn([i, 'amount'], newAmount);
    }
  }

  if ('size' in items ? items.size === 2 : items.length === 2) {
    // 2 items
    const otherIndex = index === 0 ? 1 : 0;
    adjustAmountAtIndex(otherIndex);
  } else {
    // multiple items
    // look for uncategorized item to change (that isn't the item you just edited)
    const uncatIndex =
      items?.findIndex(
        (item: SplitItemProps, currentIndex: number) => currentIndex !== index && item?.coa?.type === 'UNCATEGORIZED',
      ) ?? -1;

    if (uncatIndex >= 0) {
      adjustAmountAtIndex(uncatIndex);
    } else {
      // add uncat item with the difference
      const newItem = transactionsTypes.mkSplitItem({
        amount: diff,
        coa: { type: 'UNCATEGORIZED', id: '0' },
      });
      const newItems = Array.isArray(items) ? items.concat(newItem) : items.push(newItem);
      newSplit = 'set' in newSplit ? newSplit.set('items', newItems) : { ...newSplit, items: newItems };
    }
  }
  return newSplit;
}

// ----------------
// removeSplitLine
// ----------------
// given a txn and the index of the splitItem to remove, perform the balancing
// and return the updated split (and category)
export function removeSplitLine(txn: Pick<TransactionProps, 'split'>, index: number) {
  assert(index !== undefined, 'FORGOT TO PASS IN AN INDEX');
  let newSplit = txn.split;

  if (!newSplit || !newSplit.items) {
    return newSplit;
  }

  const items = newSplit.items;

  const itemToRemove = Array.isArray(items) ? items[index] : items.get(index);

  // exit early if deleting a zero amount
  if (itemToRemove?.amount === 0) {
    return 'deleteIn' in newSplit ? newSplit.deleteIn(['items', index]) : { ...newSplit, items: null };
  }

  function adjustAmountAtIndex(i: number) {
    if (!newSplit || !newSplit.items) {
      return;
    }

    const otherItem = Array.isArray(items) ? items[i] : items.get(i);
    if (!otherItem) {
      return;
    }
    const newAmount = Number(Number(Number(otherItem.amount) + Number(itemToRemove?.amount || 0)).toFixed(2));
    if ('setIn' in newSplit) {
      newSplit = newSplit.setIn(['items', i, 'amount'], newAmount);
    } else if (Array.isArray(newSplit.items)) {
      newSplit.items[i].amount = newAmount;
    }
  }

  // push the amount somewhere else

  // look for uncategorized item to change (that isn't the item to delete)
  const uncatIndex =
    items?.findIndex(
      (item: SplitItemProps, currentIndex: number) => currentIndex !== index && item?.coa?.type === 'UNCATEGORIZED',
    ) ?? -1;

  if (uncatIndex !== -1) {
    // uncat found
    adjustAmountAtIndex(uncatIndex);
  } else {
    // look for same category item (that isn't the item to delete)
    const sameCatIndex =
      items?.findIndex(
        (item: SplitItemProps, currentIndex: number) =>
          currentIndex !== index &&
          item?.coa?.type === itemToRemove?.coa?.type &&
          item?.coa?.id === itemToRemove?.coa?.id,
      ) ?? -1;

    if (sameCatIndex !== -1) {
      // same cat found
      adjustAmountAtIndex(sameCatIndex);
    } else {
      // push to the top
      const defaultIndex = index === 0 ? 1 : 0;
      adjustAmountAtIndex(defaultIndex);
    }
  }
  if ('deleteIn' in newSplit) {
    newSplit = newSplit.deleteIn(['items', index]);
  } else if (Array.isArray(newSplit.items)) {
    newSplit.items.splice(index, 1);
  }

  return newSplit;
}

// Above functions can most likely be deleted when we phase out legacy splits
// -----

export function sumSplitItems(split?: SplitProps | null) {
  let sum = 0;
  if (split && split.items) {
    split.items.forEach((item) => {
      sum += Number(item.amount);
    });
  }
  return Number(sum.toFixed(2));
}
